import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';

import { seesN8nTag } from './routeState';

/**
 * Reglas y estado del Form B que no son pintado.
 *
 * Gemelo de `registerForm.ts` y por el mismo motivo: la validacion de cliente se lee de un
 * vistazo junto a la de servidor (`src/Command/RequestGroupAccessHandler.php`). El cliente
 * valida por UX —error inline al enviar, foco al primero, como M2b— y el servidor porque es el
 * unico que manda; cuando discrepan gana el servidor y el formulario lo muestra igual.
 */

/** Campos del formulario, en el orden en que aparecen (el foco al primer error lo usa). */
export const FIELD_ORDER = ['origin', 'lowcode', 'automation', 'excel', 'n8n', 'goals', 'context', 'rules'] as const;

export type FieldName = (typeof FIELD_ORDER)[number];
export type Errors = Partial<Record<FieldName, string>>;

/** Los tres campos multi-seleccion. Las demas son de valor unico. */
export const MULTI: FieldName[] = ['lowcode', 'automation', 'goals'];

/**
 * Clave de «Ninguna aun». Tiene que coincidir con `GroupRequestOptions::NONE`: es la unica
 * opcion con semantica propia (excluyente dentro de su lista).
 */
export const NONE = 'ninguna';

export type RequestOptions = {
  origin: string[];
  lowcode: string[];
  automation: string[];
  excel: string[];
  n8n: string[];
  goals: string[];
};

export type RequestStatus = 'pending' | 'approved' | 'rejected';

/**
 * Estado de la puerta B tal como lo sirve `Api\AddGateState`, para el actor de esta sesion.
 */
export type GateState = {
  configured: boolean;
  isMember: boolean;
  request: { id: number; status: RequestStatus; createdAt: string | null; decidedAt: string | null } | null;
};

const EMPTY_GATE: GateState = { configured: false, isMember: false, request: null };

/**
 * El estado de la puerta, siempre desde `app.forum`.
 *
 * Se lee EN CADA USO y no se guarda en una variable de modulo: tras enviar el formulario
 * `setGate()` lo reescribe, y las cuatro superficies que lo consultan (n8card, D7, exito del
 * Form A y esta pagina) tienen que ver el cambio sin recargar.
 */
export function gate(): GateState {
  const raw = app.forum.attribute<Partial<GateState>>('umbralComunidadGate');

  if (!raw) return EMPTY_GATE;

  return {
    configured: !!raw.configured,
    isMember: !!raw.isMember,
    request: raw.request || null,
  };
}

/**
 * Refleja en el cliente lo que el servidor acaba de decidir.
 *
 * El payload del foro se sirve UNA vez, al arrancar la SPA; sin esto, mandar la solicitud
 * dejaria la n8card del sidebar ofreciendo «Solicitar acceso» hasta la siguiente recarga
 * completa. `pushAttributes` es la via del propio core para actualizar un modelo ya cargado.
 */
export function setGate(next: Partial<GateState>): void {
  app.forum.pushAttributes({ umbralComunidadGate: { ...gate(), ...next } });
}

/**
 * ¿Esta sesion YA tiene el contenido del grupo delante?
 *
 * DOS SEÑALES, Y HACEN FALTA LAS DOS (PKG-FORO-UX-BUNDLE · frente D).
 *
 *   - `gate().isMember` — pertenece al grupo. Lo unico que concede la aprobacion o el canje.
 *   - `seesN8nTag()`    — la categoria restringida le llega en el payload, es decir tiene
 *                         `tagN.viewForum` de verdad. Los ADMINISTRADORES entran por aqui:
 *                         `whereHasPermission()` de Flarum devuelve la consulta entera sin
 *                         filtrar cuando el actor es admin, asi que ven TODAS las etiquetas
 *                         sin pertenecer a ningun grupo. Tambien cubre a un moderador al que
 *                         se le haya dado el permiso por otra via.
 *
 * NINGUNA IMPLICA A LA OTRA, y las dos direcciones han pasado de verdad: T06-OPS encontro en
 * produccion miembros del grupo SIN el permiso de su categoria (`isMember` sin `seesN8nTag`),
 * y el admin unico del foro es el caso contrario (`seesN8nTag` sin `isMember`).
 *
 * EL BUG QUE CIERRA. `canRequest()` miraba solo `isMember`, mientras que D7 ya calculaba el
 * acceso con las dos — o sea que las «cuatro superficies que deciden lo mismo» no lo decidian.
 * Consecuencia medida sobre el admin: la n8card del sidebar le ofrecia «Solicitar acceso» y,
 * en cuanto probaba el formulario una vez, se le quedaba fija la tarjeta «Tu solicitud al
 * grupo esta en revision» — una solicitud que nadie va a aprobarle porque ya esta dentro.
 */
export function hasN8nAccess(): boolean {
  return gate().isMember || seesN8nTag();
}

/**
 * ¿Hay que ofrecer la puerta B a esta sesion?
 *
 * Tres condiciones, y las tres importan: que un admin la haya configurado (sin grupo no hay
 * nada que conceder), que haya sesion (Spec §10: la puerta B no registra, checklist 19) y que
 * no tenga ya el acceso (el CTA se oculta si ya es miembro, Spec §10 — ver `hasN8nAccess()`
 * para por que «ya es miembro» no es solo `isMember`).
 */
export function canRequest(): boolean {
  const state = gate();

  return state.configured && !!app.session.user && !hasN8nAccess();
}

/** Solicitud vigente de esta sesion, o null. */
export function currentRequest(): GateState['request'] {
  return gate().request;
}

export function requestPath(): string {
  return app.route('umbral-comunidad.solicitud');
}

export function requestOptions(): RequestOptions {
  const raw = app.forum.attribute<Partial<RequestOptions>>('umbralComunidadRequestOptions') || {};

  return {
    origin: raw.origin || [],
    lowcode: raw.lowcode || [],
    automation: raw.automation || [],
    excel: raw.excel || [],
    n8n: raw.n8n || [],
    goals: raw.goals || [],
  };
}

export function contextMax(): number {
  return app.forum.attribute<number>('umbralComunidadContextMax') || 300;
}

export type FormValues = {
  origin: string;
  lowcode: string[];
  automation: string[];
  excel: string;
  n8n: string;
  goals: string[];
  context: string;
  rules: boolean;
};

export function emptyValues(): FormValues {
  return { origin: '', lowcode: [], automation: [], excel: '', n8n: '', goals: [], context: '', rules: false };
}

const ERRORS = 'umbral-comunidad.forum.solicitud.errors';

/**
 * Alterna una opcion respetando la exclusividad de «Ninguna aun».
 *
 * Marcar «ninguna» borra el resto y marcar cualquier otra borra «ninguna». Se hace aqui y no
 * solo en el servidor porque un formulario que ACEPTA la contradiccion y luego la rechaza al
 * enviar es peor que uno que no la deja construir: la persona no ve un error, ve que la
 * interfaz le corrige la idea mientras la escribe.
 */
export function toggleMulti(list: string[], value: string): string[] {
  if (value === NONE) {
    return list.indexOf(NONE) >= 0 ? [] : [NONE];
  }

  const next = list.filter((item) => item !== NONE);
  const at = next.indexOf(value);

  if (at >= 0) next.splice(at, 1);
  else next.push(value);

  return next;
}

/** Validacion de cliente. Espeja la del servidor. */
export function validate(values: FormValues, max: number): Errors {
  const t = (key: string) => extract(`${ERRORS}.${key}`);
  const errors: Errors = {};

  if (!values.origin) errors.origin = t('origin');
  if (!values.lowcode.length) errors.lowcode = t('lowcode');
  if (!values.automation.length) errors.automation = t('automation');
  if (!values.excel) errors.excel = t('excel');
  if (!values.n8n) errors.n8n = t('n8n');
  if (!values.goals.length) errors.goals = t('goals');
  // El limite lo pone el servidor (`GroupRequestOptions::CONTEXT_MAX`), asi que el mensaje lo
  // interpola en vez de repetirlo: cambiarlo alli no debe dejar aqui un numero que miente.
  if (values.context.length > max) {
    errors.context = extractText(app.translator.trans(`${ERRORS}.context`, { max }));
  }
  if (!values.rules) errors.rules = t('rules');

  return errors;
}

/**
 * Punteros JSON:API -> campos del formulario.
 *
 * Nuestro handler valida los atributos en plano, asi que los punteros llegan como
 * `/data/attributes/<campo>`. Lo que no se reconozca se devuelve aparte y se muestra entero:
 * perder un mensaje del servidor por no saber donde ponerlo seria peor que ensenarlo suelto.
 */
export function mapServerErrors(response: any): { errors: Errors; unmapped: string[] } {
  const errors: Errors = {};
  const unmapped: string[] = [];
  const list: any[] = (response && response.errors) || [];
  const known = new Set<string>([...FIELD_ORDER]);

  list.forEach((error) => {
    const pointer: string = (error && error.source && error.source.pointer) || '';
    const detail: string = (error && error.detail) || '';
    const field = pointer.replace('/data/attributes/', '').split('.')[0];

    if (field && known.has(field)) errors[field as FieldName] = detail || extract(`${ERRORS}.generic`);
    else if (detail) unmapped.push(detail);
  });

  return { errors, unmapped };
}

/**
 * Los conflictos de estado (409/503) no traen `detail`, solo `code` — el formateador del core
 * no lo pone y el texto vive en `locale/es.yml`, para que salga en el idioma de quien mira.
 * Devuelve null si el error no es uno de los nuestros: ahi manda la alerta generica de Flarum.
 */
export function conflictMessage(response: any): string | null {
  const code: string = (response && response.errors && response.errors[0] && response.errors[0].code) || '';

  const known: Record<string, string> = {
    umbral_already_member: 'already_member',
    umbral_request_pending: 'already_pending',
    umbral_group_not_configured: 'not_configured',
    umbral_rate_limited: 'rate_limited',
    umbral_n8n_access_degraded: 'access_degraded',
    // PKG-FORO-PRODUCT-GATES · decision D. FALTABA, y se veia en produccion: el servidor corta
    // la solicitud de quien no ha confirmado el correo (`RequestGroupAccessHandler.php`, la
    // PRIMERA puerta tras `assertRegistered()`), pero el codigo no estaba en este mapa, asi
    // que `conflictMessage()` devolvia null y la pantalla caia en la alerta generica de
    // Flarum — «¡Caramba! Algo salio mal». Un muro sin explicacion delante de una accion que
    // la persona SI puede desbloquear, y ademas asustando con un fallo que no existe.
    umbral_email_unconfirmed: 'email_unconfirmed',
  };

  return known[code] ? extract(`${ERRORS}.${known[code]}`) : null;
}

/**
 * ¿Este error es el veto por correo sin confirmar?
 *
 * Se expone aparte del mensaje porque la pantalla tiene que hacer DOS cosas con este codigo, no
 * una: pasar al estado «confirma tu correo» —el unico que trae la accion de reenvio— y ademas
 * decir por que. El texto sigue saliendo de `conflictMessage()`, que es donde vive el mapa;
 * esto solo responde «¿es este el caso?». `conflictGate()` no sirve para lo primero: solo sabe
 * describir cambios de `GateState`, y «sin confirmar» no es un campo de esa forma (no viene del
 * payload del foro, viene del propio usuario de la sesion).
 */
export function isEmailUnconfirmedConflict(response: any): boolean {
  const code: string = (response && response.errors && response.errors[0] && response.errors[0].code) || '';

  return code === 'umbral_email_unconfirmed';
}

/** El estado que deja un conflicto, para que la pantalla se corrija sola tras un 409. */
export function conflictGate(response: any): Partial<GateState> | null {
  const code: string = (response && response.errors && response.errors[0] && response.errors[0].code) || '';

  if (code === 'umbral_already_member') return { isMember: true };
  if (code === 'umbral_request_pending') {
    return { request: { id: 0, status: 'pending', createdAt: null, decidedAt: null } };
  }

  return null;
}

function extract(key: string): string {
  return extractText(app.translator.trans(key));
}
